// When the launch screen may close. Pure: the native app's loader uses the
// same rule, so both apps feel the same.

export interface LoaderConfig {
	readonly minMs: number;
	/** Reading speed the fact is timed for: a slow, young reader. */
	readonly wordsPerSecond: number;
	readonly settleMs: number;
	/** How long the launch waits on the current unit's recordings before giving up. */
	readonly maxWaitMs: number;
	readonly lookaheadUnits: number;
}

export const DEFAULT_LOADER_CONFIG: LoaderConfig = {
	minMs: 3000,
	wordsPerSecond: 2,
	settleMs: 1000,
	maxWaitMs: 15000,
	lookaheadUnits: 5,
};

/** A value outside these is a typo in the flag, not a setting, so it falls back to the default. */
const BOUNDS: Record<keyof LoaderConfig, readonly [number, number]> = {
	minMs: [0, 30_000],
	wordsPerSecond: [0.5, 10],
	settleMs: [0, 10_000],
	maxWaitMs: [0, 60_000],
	lookaheadUnits: [0, 50],
};

export function parseLoaderConfig(payload: unknown): LoaderConfig {
	let p = payload;
	if (typeof p === "string") {
		try {
			p = JSON.parse(p);
		} catch {
			return DEFAULT_LOADER_CONFIG;
		}
	}
	if (typeof p !== "object" || p === null || Array.isArray(p)) return DEFAULT_LOADER_CONFIG;
	const raw = p as Record<string, unknown>; // a non-null, non-array object, checked above
	const out: { -readonly [K in keyof LoaderConfig]: number } = { ...DEFAULT_LOADER_CONFIG };
	// Object.keys widens to string[]; BOUNDS's keys are exactly LoaderConfig's.
	for (const k of Object.keys(BOUNDS) as (keyof LoaderConfig)[]) {
		const v = raw[k];
		const [lo, hi] = BOUNDS[k];
		if (typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi)
			out[k] = k === "lookaheadUnits" ? Math.floor(v) : v;
	}
	return out;
}

export function readingTimeMs(text: string, c: LoaderConfig): number {
	const t = text.trim();
	const words = t ? t.split(/\s+/).length : 0;
	return Math.max(c.minMs, Math.round((words / c.wordsPerSecond) * 1000) + c.settleMs);
}

export type CloseReason = "reading" | "download" | "timeout" | "boot";

export interface GateInput {
	readonly now: number;
	readonly shownAt: number;
	/** When the app behind the screen finished loading; null while it has not. */
	readonly bootDoneAt: number | null;
	readonly priorityDoneAt: number | null;
	readonly readMs: number;
	readonly maxWaitMs: number;
}

export type GateDecision =
	| { readonly close: true; readonly reason: CloseReason }
	/** `recheckInMs` null: nothing is timed, the next input change decides. */
	| { readonly close: false; readonly recheckInMs: number | null };

export function launchGate(g: GateInput): GateDecision {
	if (g.bootDoneAt === null) return { close: false, recheckInMs: null };
	const elapsed = g.now - g.shownAt;
	const readLeft = g.readMs - elapsed;
	const waitLeft = g.priorityDoneAt === null ? g.maxWaitMs - elapsed : 0;
	const left = Math.max(readLeft, waitLeft);
	if (left > 0) return { close: false, recheckInMs: left };
	if (g.priorityDoneAt === null) return { close: true, reason: "timeout" };
	const readEnd = g.shownAt + g.readMs;
	if (g.bootDoneAt >= readEnd && g.bootDoneAt >= g.priorityDoneAt)
		return { close: true, reason: "boot" };
	return { close: true, reason: g.priorityDoneAt > readEnd ? "download" : "reading" };
}
